import { authService } from "@/app/services/AuthService";
import {
  reporterInstance,
  ReportScene,
  reportScene,
} from "@/app/services/DataReporter";
import { Toast } from "@douyinfe/semi-ui";
import Locale from "@/app/locales";
const TOKEN_EXPIRATION = "30002";
const NO_AUTHORITY = "30003";
const SUCCESS_CODE = "000000";

// 重试配置
const MAX_RETRY_ATTEMPTS = 1;
const RETRY_DELAY_BASE = 1000; // 基础延迟时间（毫秒）
const SERVICE_ERROR_MSG = "service error";
export const baseUrl =
  process.env.NODE_ENV === "development"
    ? ""
    : process.env.NEXT_PUBLIC_API_HOST;

interface RequestOptions extends RequestInit {
  crossDomain?: boolean;
  withCredentials?: boolean;
  params?: Record<string, unknown>;
  timeout?: number;
  // 不上报error，比方说已读未读接口，上报消息接口等，若抛出异常，不提示错误，但是需要收集数据
  doNotCacheError?: boolean;
  // 重试次数
  retryAttempts?: number;
  // 重试间隔时间
  retryDelayBase?: number;
  // 请求来源于重试用于上报重试是否成功
  fromRetry?: boolean;
  // 不提示错误
  notToastError?: boolean;
}

/**
 * 封装的请求方法
 * @param url 请求地址
 * @param options 请求配置
 * @returns Promise
 */
async function request<T = unknown>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    params,
    timeout = 60000,
    retryAttempts = MAX_RETRY_ATTEMPTS,
    retryDelayBase = RETRY_DELAY_BASE,
    ...fetchOptions
  } = options;

  // 处理 URL 参数
  // 如果 URL 包含 gw，使用特定的 baseUrl
  const finalBaseUrl = url.includes("gw")
    ? process.env.NEXT_PUBLIC_ROBOT_API_HOST // "https://test-support.bambulab.net"
    : baseUrl;
  let finalUrl = url.startsWith("http") ? url : `${finalBaseUrl}${url}`;

  if (params) {
    const queryString = Object.entries(params)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      )
      .join("&");

    finalUrl = `${url}${url.includes("?") ? "&" : "?"}${queryString}`;
  }

  // 默认请求头
  const headers = new Headers(fetchOptions.headers || {});
  if (!headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");

  // 只在有 Token 时才添加 Authorization 头
  const token = authService.getToken();
  if (token) {
    headers.set("Authorization", `${token}`);
    headers.set("X-IM-Auth", `${token}`);
  }

  // 超时处理
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(finalUrl, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // 处理 HTTP 错误状态
    if (!response.ok) {
      // 创建可序列化的错误详情对象
      const errorDetails = {
        url: finalUrl,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        method: options.method || "GET",
      };

      // 上报具体的 HTTP 错误，包含状态码信息
      reportScene(
        ReportScene.API_ERROR,
        JSON.stringify({
          ...errorDetails,
          timestamp: new Date().toISOString(),
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      );

      if (response.status === 401 || response.status === 403) {
        // 401/403 不再自动登出，让用户决定是否登录
        authService.logout("token_expired");
        throw new Error("Unauthorized - Please login to continue");
      } else {
        throw new Error(
          `HTTP Error: ${response.status} ${response.statusText}`,
        );
      }
    }

    // 解析 JSON 响应
    const { data, code, msg } = await response.json();

    // 如果 token 过期或没有权限，提示需要登录
    // 不再自动跳转，让用户自主选择
    if (code === TOKEN_EXPIRATION || code === NO_AUTHORITY) {
      // 传递 token_expired 原因，触发相应的事件
      authService.logout("token_expired");
      throw new Error(msg || "Authentication required");
    }

    if (code !== SUCCESS_CODE) {
      reportScene(
        ReportScene.API_ERROR,
        JSON.stringify({
          url,
          options,
          code,
        }),
      );
      reporterInstance.flush();
      throw new Error(msg);
    }

    if (options.fromRetry) {
      reportScene(
        ReportScene.API_RETRY_SUCCESS,
        JSON.stringify({ url, options }),
      );
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);

    // 判断是否需要重试
    const shouldRetry =
      error instanceof Error &&
      error.name === "TypeError" &&
      error.message.includes("fetch") &&
      retryAttempts > 0;

    if (shouldRetry) {
      // 计算延迟时间（指数退避）
      const delay = retryDelayBase * (MAX_RETRY_ATTEMPTS - retryAttempts + 1);

      // 等待后重试
      await new Promise((resolve) => setTimeout(resolve, delay));
      // 递归重试，减少重试次数
      return request<T>(url, {
        ...options,
        retryAttempts: retryAttempts - 1,
        retryDelayBase,
        fromRetry: true,
      });
    }

    // 构建详细的错误信息对象
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorInfo: Record<string, any> = {
      url: finalUrl,
      method: options.method || "GET",
      timestamp: new Date().toISOString(),
    };

    if (error instanceof Error) {
      errorInfo.type = error.name || "Error";
      errorInfo.message = error.message;

      // 特殊处理网络相关错误
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        errorInfo.type = "NetworkError";
        errorInfo.online =
          typeof navigator !== "undefined" ? navigator.onLine : true;
        errorInfo.userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : "";
      } else if (error.name === "AbortError") {
        errorInfo.type = "Timeout";
        if (!options.notToastError) {
          Toast.error(Locale.common.requestTimeout);
        }
      } else {
        if (!options.notToastError) {
          Toast.error(error.message || SERVICE_ERROR_MSG);
        }
      }
    } else {
      errorInfo.type = "UnknownError";
      errorInfo.message = String(error);
      // 吞掉异常，尝试自动重试
      // Toast.error(SERVICE_ERROR_MSG);
    }

    // 除非明确指定不缓存错误，否则都进行上报
    if (!options.doNotCacheError) {
      reportScene(ReportScene.API_ERROR, JSON.stringify(errorInfo));
    }

    throw error;
  }
}

/**
 * GET 请求
 */
request.get = <T = unknown>(url: string, options?: RequestOptions) => {
  return request<T>(url, { ...options, method: "GET" });
};

/**
 * POST 请求
 */
request.post = <T = unknown>(
  url: string,
  data?: Record<string, unknown> | Array<unknown>,
  options?: RequestOptions,
) => {
  return request<T>(url, {
    ...options,
    method: "POST",
    body: JSON.stringify(data),
  });
};

/**
 * 文件上传请求
 * @param url 上传地址
 * @param file 文件对象或 FormData
 * @param options 请求配置（注意：不要手动设置 Content-Type）
 * @returns Promise
 */
request.upload = async <T = unknown>(
  url: string,
  file: File | FormData,
  options?: Omit<RequestOptions, "body"> & {
    fieldName?: string;
  },
): Promise<T> => {
  const {
    fieldName = "file",
    timeout = 120000,
    ...fetchOptions
  } = options || {};

  // 处理 URL
  const finalUrl = url.startsWith("http")
    ? url
    : `${process.env.NEXT_PUBLIC_API_HUB_HOST}${url}`;

  // 创建 FormData
  let formData: FormData;
  if (file instanceof FormData) {
    formData = file;
  } else {
    formData = new FormData();
    formData.append(fieldName, file);
  }

  // 设置请求头（重要：不要设置 Content-Type，让浏览器自动设置 multipart/form-data 和 boundary）
  const headers = new Headers(fetchOptions.headers || {});

  // 删除可能手动设置的 Content-Type，以确保浏览器自动设置
  headers.delete("Content-Type");

  // 只在有 Token 时才添加 Authorization 头
  const token = authService.getToken();
  if (token) {
    headers.set("Authorization", `${token}`);
  }

  // 超时处理
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(finalUrl, {
      ...fetchOptions,
      method: "POST",
      headers,
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // 处理 HTTP 错误状态
    if (!response.ok) {
      const errorDetails = {
        url: finalUrl,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        method: "POST",
      };

      reportScene(
        ReportScene.API_ERROR,
        JSON.stringify({
          ...errorDetails,
          timestamp: new Date().toISOString(),
          userAgent:
            typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      );

      if (response.status === 401 || response.status === 403) {
        authService.logout("token_expired");
        throw new Error("Unauthorized - Please login to continue");
      } else {
        throw new Error(
          `HTTP Error: ${response.status} ${response.statusText}`,
        );
      }
    }

    // 解析 JSON 响应
    const { data, code, msg } = await response.json();

    if (code === TOKEN_EXPIRATION || code === NO_AUTHORITY) {
      authService.logout("token_expired");
      throw new Error(msg || "Authentication required");
    }

    if (code !== SUCCESS_CODE) {
      reportScene(
        ReportScene.API_ERROR,
        JSON.stringify({
          url: finalUrl,
          code,
        }),
      );
      reporterInstance.flush();
      throw new Error(msg);
    }

    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorInfo: Record<string, any> = {
      url: finalUrl,
      method: "POST",
      timestamp: new Date().toISOString(),
    };

    if (error instanceof Error) {
      errorInfo.type = error.name || "Error";
      errorInfo.message = error.message;

      if (error.name === "TypeError" && error.message.includes("fetch")) {
        errorInfo.type = "NetworkError";
        errorInfo.online =
          typeof navigator !== "undefined" ? navigator.onLine : true;
        errorInfo.userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : "";
      } else if (error.name === "AbortError") {
        errorInfo.type = "Timeout";
        Toast.error("Request timeout, please try again.");
      } else {
        Toast.error(error.message || SERVICE_ERROR_MSG);
      }
    } else {
      errorInfo.type = "UnknownError";
      errorInfo.message = String(error);
      Toast.error(SERVICE_ERROR_MSG);
    }

    if (!options?.doNotCacheError) {
      reportScene(ReportScene.API_ERROR, JSON.stringify(errorInfo));
    }

    throw error;
  }
};

/**
 * PUT 请求
 */
request.put = <T = unknown>(
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions,
) => {
  return request<T>(url, {
    ...options,
    method: "PUT",
    body: JSON.stringify(data),
  });
};

/**
 * DELETE 请求
 */
request.delete = <T = unknown>(url: string, options?: RequestOptions) => {
  return request<T>(url, { ...options, method: "DELETE" });
};

export default request;
